import { Dimmer, Grid, Loader, Message, Table } from "semantic-ui-react";
import { formatNumberToScientific } from "smart-decimal-format";
import { useGetBillOfMaterialSummary } from "../../../../api/billOfMaterialSummary";
import { useGetProductSummary } from "../../../../api/productSummary";
import { images } from "../../../../assets/images";
import { CommonTable } from "../../../../components/commonTable/CommonTable";
import { ImageView } from "../../../../components/ImageView/ImageView";
import ProgressBar from "../../../../components/progressBar/ProgressBar";
import { TitleView } from "../../../../components/titleView/TitleView";
import {
  COMPANY_NAME,
  ImpactAssessmentMethodText,
  NOT_APPLICABLE,
  ProductSummaryTableCol
} from "../../../../config/constants";
import { IngredientDataType } from "../../../../config/enums/IngredientType.enum";
import { ROLES } from "../../../../config/permission-maps";
import { getUserRolesInLocal } from "../../../../utils/cacheStorage";
import { humanize } from "../../../../utils/utils";

const IngredientImpactTab = ({ ingredientDetails }: any) => {
  const localUserRole = getUserRolesInLocal();
  const checkIsUserSupplier = localUserRole?.[0] === ROLES.supplier;
  const isSupplier =
    ingredientDetails?.supplierIngredientDetails?.data_type === IngredientDataType.SUPPLIER;

  const ingredientId = ingredientDetails?.ingredient_id;
  const supplierId = ingredientDetails?.supplier_id;
  const productId = ingredientDetails?.supplierIngredientDetails?.product_summary?.productId;
  const salesUnitId = ingredientDetails?.supplierIngredientDetails?.product_summary?.salesUnitId;

  const productSummaryData = useGetProductSummary({
    productId,
    salesUnitId,
    isSupplier
  });

  const billOfMaterialSummaryData = useGetBillOfMaterialSummary({
    ingredientId,
    supplierId,
    isSupplier
  });

  const productSummary = isSupplier ? productSummaryData.data : billOfMaterialSummaryData.data;

  const isLoading =
    (isSupplier && productSummaryData.isFetching) ||
    (!isSupplier && billOfMaterialSummaryData.isFetching);

  const { measure: climateChangeMeasure, unit: climateChangeUnit } =
    productSummary?.climateChange || {};

  const cycleStageData: any = productSummary?.cycleStages || {};
  const totalImpact = productSummary?.totalImpact || [];

  const cycleStages = Object.entries(cycleStageData).map(([key, value]) => ({
    key,
    value
  }));

  const loadTableData = () => {
    return totalImpact.map((data: any, index: any) => {
      return (
        <Table.Row className="tbleR" key={index}>
          <Table.Cell>
            <p>{data.indicator}</p>
          </Table.Cell>
          <Table.Cell>
            <p>{formatNumberToScientific(data.measure, 5)}</p>
          </Table.Cell>
          <Table.Cell>
            <p>{data.unit}</p>
          </Table.Cell>
          <Table.Cell>
            <Grid>
              <Grid.Column
                computer={16}
                tablet={16}
                mobile={16}
                key={index}
                className="alignRight addViewDetails"
              ></Grid.Column>
            </Grid>
          </Table.Cell>
        </Table.Row>
      );
    });
  };

  if (isLoading) {
    return (
      <Dimmer active>
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  return (
    <>
      {productSummary ? (
        !checkIsUserSupplier ? (
          <>
            <Grid className="backgroundMain productProgressMain">
              <Grid.Column computer={16} tablet={16} mobile={16}>
                <Grid>
                  <Grid.Column computer={4} tablet={16} mobile={16} className="productDetailsView">
                    <div>
                      <ImageView
                        addRandomId={false}
                        src={ingredientDetails?.ingredient_image_path || images.notFound}
                        height={150}
                      />
                      <div className="productDetailsViewOther">
                        <p className="productNameDetails">{ingredientDetails?.ingredient_name}</p>
                      </div>
                    </div>
                  </Grid.Column>

                  <Grid.Column computer={6} tablet={16} mobile={16}>
                    <Grid className="backgroundMain">
                      <Grid.Column computer={16} tablet={16} mobile={16}>
                        <TitleView title="Climate Change Impact" />
                        <p className="viewNumberOfClimate">{climateChangeMeasure?.toFixed(2)}</p>
                        <p className="viewNumberOfClimateSub">{climateChangeUnit + " per kg"}</p>
                        <div className="climateDetailsView">
                          {COMPANY_NAME}
                          {ImpactAssessmentMethodText}
                        </div>
                        <h5 className="viewBottomClimateDetails" hidden>
                          DQR : 3.61
                        </h5>
                      </Grid.Column>
                    </Grid>
                  </Grid.Column>

                  <Grid.Column computer={6} tablet={16} mobile={16}>
                    <Grid className="backgroundMain">
                      <Grid.Column computer={16} tablet={16} mobile={16}>
                        <TitleView title="Impact by life cycle stages" />
                        {cycleStages.map((data, index) => (
                          <ProgressBar
                            key={index}
                            imageView={true}
                            // @ts-ignore
                            image={images[data.key]}
                            title={humanize(data.key)}
                            count={
                              data.value === NOT_APPLICABLE
                                ? data.value
                                : `${parseFloat(data.value as string).toFixed(2)}%`
                            }
                            percentage={data.value === NOT_APPLICABLE ? 0 : (data.value as number)}
                          />
                        ))}
                      </Grid.Column>
                    </Grid>
                  </Grid.Column>
                </Grid>
              </Grid.Column>
            </Grid>

            <CommonTable tableHeaderData={ProductSummaryTableCol}>{loadTableData()}</CommonTable>
          </>
        ) : (
          <Message color="yellow">Calculated: Upgrade to see results</Message>
        )
      ) : (
        <Message color="yellow">Data collection in progress</Message>
      )}
    </>
  );
};

export default IngredientImpactTab;
